import React, { useEffect, useState, useCallback } from "react";
import {
  Crown, Zap, ArrowRight, ArrowDown, Check, X, AlertTriangle,
  Users, Layers, BookOpen, UserCog, Shield,
  Calendar, TrendingUp, Clock, Sparkles,
  ChevronRight, Star, Loader, HardDrive, Percent, BadgeIndianRupee,
  Download, History, Tag, Handshake, Mail, Phone,
} from "lucide-react";
import { useNavigate } from "react-router-dom"; // eslint-disable-line no-unused-vars
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import Shimmer from "../Common/Shimmer";
import useHostedCheckout from "../../hooks/useHostedCheckout";
import HostedCheckoutModal from "./HostedCheckoutModal";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); billing.scss deleted.
//
// 🔴 THE PALETTE FINDING: this sheet was HALF converted already, and the two
// halves disagreed on screen. Every soft wash was written against the brand
// (`hsl(var(--primary) / 0.06)`) while the ink sitting on it was a literal
// `#2563eb` — so the trial banner painted a violet wash with blue text, the
// "Recommended" card drew a blue border inside a violet glow, and the receipt
// button had a violet border with blue ink. Three different accent literals were
// in play (`#2563eb` blue-600, `#6366f1` indigo-500, `#7c3aed` violet-600) on the
// one page in the product where a customer decides whether to pay us.
//
// All three are `--primary` now, which is also the token `applyBrandTokens`
// rewrites per institute — so the page follows the customer's colour, which it
// could not do while the ink was a hex.
//
// Status colours map by ROLE, not by hue: the four pills take the contract's
// three-token shape (`-soft` background, `-fg` ink — TOKEN_CONTRACT §4.4), and
// the ink on every near-white wash is the `-fg` step rather than the mid-tone
// the sheet was using, which measures under 4.5:1 on its own background.
//
// ⚠️ The gradients were HUE ramps (`#2563eb → #7c3aed`, blue to violet) and
// cannot survive as such: with one brand hue there is no second hue to ramp to.
// They are lightness ramps off `--primary` now, via the `color-mix()` idiom
// UpgradeBanner / NotificationToast already use. The one gradient that was
// ALREADY primary at both ends (`.bp-alert--trial`, primary-6% → violet-6%)
// collapses to a flat `tw-bg-primary/[0.06]` — once both stops are the same
// token the two-stop form computes to exactly the same paint.
//
// ⚠️ The gradient does NOT live in the button base string. `tw-bg-[linear-…]`
// sets background-IMAGE and `tw-bg-surface-alt` sets background-COLOR, so the
// "current plan" variant would have kept the brand gradient underneath its grey.
// CARD_BTN_BASE carries everything except the background; each variant adds its
// own. Same trap `collect-fee-v2` hit with padding, one property along.
//
// ⚠️ Radii read off the SCSS against the REMAPPED scale in tailwind.config.js:
// 8px is `tw-rounded-sm`, 10px is `tw-rounded-md`, 12px is `tw-rounded`, 16px is
// `tw-rounded-lg`. 6px, 4px and 3px have no step and stay arbitrary.
//
// The page loader was a spinner; it is a Shimmer mirroring the layout now
// (Phase 6.5). The one spinner left is the in-flight payment notice, which is
// genuinely indeterminate — that is what spinners are still for.

const PAGE = "tw-flex tw-flex-col tw-gap-6";

// ── Header ──
const HEADER = "tw-flex tw-items-center tw-justify-between tw-gap-4";
const HEADER_LEFT = "tw-flex tw-items-start tw-gap-3";
const HEADER_ICON =
  "tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--primary-solid)),color-mix(in_srgb,hsl(var(--primary-solid))_78%,black))] " +
  "tw-text-primary-solid-fg";
const HEADER_TITLE =
  "tw-m-0 tw-text-2xl tw-font-medium tw-leading-[1.2] tw-tracking-[-0.02em] tw-text-content";
const HEADER_SUBTITLE = "tw-mb-0 tw-mt-0.5 tw-text-[13px] tw-text-content-subtle";

// ── Alert banners ──
const ALERT =
  "tw-flex tw-items-center tw-gap-3.5 tw-rounded tw-border tw-border-solid tw-px-5 tw-py-3.5 " +
  "tw-text-[13px] max-[768px]:tw-flex-wrap";
const ALERT_TONE = {
  trial: "tw-border-primary/15 tw-bg-primary/[0.06] tw-text-primary-soft-fg",
  info: "tw-border-primary/15 tw-bg-primary/[0.06] tw-text-primary-soft-fg",
  danger: "tw-border-danger/15 tw-bg-danger/[0.06] tw-text-danger-fg",
  success: "tw-border-success/15 tw-bg-success/[0.06] tw-text-success-fg",
};
const ALERT_BODY = "tw-flex tw-flex-1 tw-flex-col tw-gap-0.5";
const ALERT_BODY_SPAN = "tw-text-xs tw-opacity-85";
const ALERT_ACTIONS = "tw-flex tw-shrink-0 tw-items-center tw-gap-2";
const ALERT_CTA =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-sm " +
  "tw-border tw-border-solid tw-border-current tw-bg-transparent tw-px-3.5 tw-py-1.5 tw-text-[13px] " +
  "tw-font-semibold tw-text-inherit tw-no-underline tw-transition-all tw-duration-150 " +
  "hover:tw-bg-primary-solid/[0.08] hover:tw-text-inherit hover:tw-no-underline";
const ALERT_CTA_PRIMARY =
  ALERT_CTA + " tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg " +
  "hover:tw-border-primary-solid hover:tw-bg-[color-mix(in_srgb,hsl(var(--primary-solid))_88%,black)] " +
  "hover:tw-text-primary-solid-fg disabled:tw-cursor-not-allowed disabled:tw-opacity-60";
const ALERT_CLOSE =
  "tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-1 tw-text-inherit tw-opacity-60 " +
  "hover:tw-opacity-100";

// ── Current-plan hero ──
const HERO =
  "tw-flex tw-flex-col tw-gap-5 tw-rounded-lg tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-raised tw-p-6";
const HERO_TOP =
  "tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-6 max-[768px]:tw-flex-col";
const HERO_INFO = "tw-flex tw-flex-col tw-gap-2";
const HERO_NAME_ROW = "tw-flex tw-items-center tw-gap-3";
const HERO_PLAN_NAME = "tw-text-xl tw-font-medium tw-tracking-[-0.01em] tw-text-content";
const HERO_STATUS =
  "tw-inline-flex tw-items-center tw-rounded-[6px] tw-px-2.5 tw-py-[0.2rem] tw-text-[11px] " +
  "tw-font-semibold tw-uppercase tw-tracking-[0.04em]";
const HERO_STATUS_TONE = {
  active: "tw-bg-success-soft tw-text-success-fg",
  trial: "tw-bg-primary-soft tw-text-primary-soft-fg",
  suspended: "tw-bg-danger-soft tw-text-danger-fg",
  inactive: "tw-bg-muted tw-text-muted-fg",
};
const HERO_PRICE = "tw-flex tw-items-baseline tw-gap-1";
const HERO_AMOUNT =
  "tw-text-[2rem] tw-font-extrabold tw-leading-none tw-tracking-[-0.02em] tw-text-content " +
  "max-[480px]:tw-text-2xl";
const HERO_PERIOD = "tw-text-[13px] tw-font-normal tw-text-content-subtle";
const HERO_DATES = "tw-flex tw-flex-col tw-gap-2";
const HERO_DATE_ITEM = "tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-text-content-muted";
const HERO_DATE_ICON = "tw-shrink-0 tw-text-content-subtle";
const HERO_RENEW_BTN =
  "tw-mt-2 tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-sm tw-border-none " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--primary-solid)),color-mix(in_srgb,hsl(var(--primary-solid))_82%,white))] " +
  "tw-px-5 tw-py-2 tw-text-[13px] tw-font-semibold tw-text-primary-solid-fg tw-transition-all tw-duration-200 " +
  "hover:tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--primary-solid))_82%,black),hsl(var(--primary-solid)))] " +
  "hover:tw-shadow-[0_4px_12px_hsl(var(--primary)/0.3)] " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

// ── Pending payment, inside the hero ──
const PENDING =
  "tw-mb-5 tw-flex tw-flex-wrap tw-items-center tw-gap-3.5 tw-rounded-md tw-border tw-border-solid " +
  "tw-border-warning/35 tw-px-4 tw-py-3.5 " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--warning)/0.12),hsl(var(--warning)/0.06))] " +
  "max-[767.98px]:tw-flex-col max-[767.98px]:tw-items-start";
const PENDING_ICON =
  "tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm " +
  "tw-bg-warning/[0.18] tw-text-warning-fg";
const PENDING_BODY = "tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5";
const PENDING_TITLE = "tw-text-sm tw-font-medium tw-text-content";
const PENDING_TEXT = "tw-text-[13px] tw-leading-[1.4] tw-text-content-muted";
// The old sheet needed `span.bp-plan-hero__pending-discount` — a compound
// selector — purely to out-specify the bare `span` rule above it. Utilities are
// all one class, so that override problem does not exist here.
const PENDING_DISCOUNT =
  "tw-mt-1 tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-success-fg";
const PENDING_PAY_BTN =
  "tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap " +
  "tw-rounded-sm tw-border-none tw-px-[1.1rem] tw-py-[0.55rem] tw-text-[13px] tw-font-semibold " +
  "tw-text-on-status tw-transition-all tw-duration-200 " +
  "tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--success))_82%,black),hsl(var(--success)))] " +
  "hover:tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--success))_64%,black),color-mix(in_srgb,hsl(var(--success))_86%,black))] " +
  "hover:tw-shadow-[0_4px_12px_hsl(var(--success)/0.3)] " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

// ── Billing-cycle toggle ──
const CYCLE_TOGGLE = "tw-flex tw-w-fit tw-gap-1 tw-rounded-md tw-bg-surface-alt tw-p-1";
const CYCLE_BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-sm tw-border-none " +
  "tw-bg-transparent tw-px-4 tw-py-[0.4375rem] tw-text-[13px] tw-font-medium tw-text-content-subtle " +
  "tw-transition-all tw-duration-150 hover:tw-text-content";
const CYCLE_BTN_ACTIVE =
  CYCLE_BTN + " tw-bg-surface-raised tw-font-semibold tw-text-content tw-shadow-card";
const CYCLE_SAVE =
  "tw-rounded-[4px] tw-px-1.5 tw-py-[0.1rem] tw-text-[10px] tw-font-medium tw-uppercase " +
  "tw-tracking-[0.04em] tw-text-on-status " +
  "tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--success))_82%,black),hsl(var(--success)))]";

// ── Sections ──
const SECTION = "tw-flex tw-flex-col tw-gap-4";
const SECTION_HEADER = "tw-flex tw-flex-wrap tw-items-center tw-gap-2";
const SECTION_HEADER_CLICKABLE =
  SECTION_HEADER + " tw-cursor-pointer tw-select-none hover:tw-opacity-80";
const SECTION_ICON = "tw-shrink-0 tw-text-primary";
const SECTION_TITLE =
  "tw-m-0 tw-text-[17px] tw-font-medium tw-tracking-[-0.01em] tw-text-content";
const SECTION_SUBTITLE =
  "tw-mb-0 tw-ml-[1.875rem] tw--mt-1 tw-w-full tw-text-[13px] tw-text-content-subtle";
const SECTION_CHEVRON =
  "tw-ml-auto tw-text-content-subtle tw-transition-transform tw-duration-200";

// ── Usage meters ──
const USAGE_GRID =
  "tw-grid tw-grid-cols-[repeat(auto-fill,minmax(200px,1fr))] tw-gap-3 " +
  "max-[768px]:tw-grid-cols-2 max-[480px]:tw-grid-cols-1";
const USAGE_METER =
  "tw-flex tw-flex-col tw-gap-2.5 tw-rounded tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-raised tw-p-4";
const USAGE_HEADER = "tw-flex tw-items-center tw-justify-between tw-gap-2";
const USAGE_LABEL =
  "tw-flex tw-items-center tw-gap-[0.4375rem] tw-text-[13px] tw-font-medium tw-text-content-muted";
const USAGE_LABEL_ICON = "tw-text-content-subtle";
const USAGE_COUNT = "tw-text-[13px] tw-text-content-subtle";
const USAGE_COUNT_STRONG = "tw-text-[15px] tw-font-medium tw-text-content";
const USAGE_TRACK = "tw-h-1.5 tw-overflow-hidden tw-rounded-[3px] tw-bg-surface-alt";
const USAGE_FILL = "tw-h-full tw-rounded-[3px] tw-transition-[width] tw-duration-[600ms] tw-ease-out";
const USAGE_FILL_TONE = {
  normal:
    "tw-bg-[linear-gradient(90deg,hsl(var(--primary)),color-mix(in_srgb,hsl(var(--primary))_82%,white))]",
  warning:
    "tw-bg-[linear-gradient(90deg,color-mix(in_srgb,hsl(var(--warning))_80%,black),hsl(var(--warning)))]",
  danger:
    "tw-bg-[linear-gradient(90deg,color-mix(in_srgb,hsl(var(--danger))_80%,black),hsl(var(--danger)))]",
};
const USAGE_ALERT =
  "tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-text-danger-fg";

// ── Feature list ──
const FEATURES_GRID =
  "tw-grid tw-grid-cols-[repeat(auto-fill,minmax(180px,1fr))] tw-gap-2 tw-rounded tw-border " +
  "tw-border-solid tw-border-line tw-bg-surface-raised tw-px-5 tw-py-4 " +
  "max-[768px]:tw-grid-cols-2 max-[480px]:tw-grid-cols-1";
const FEATURE_ITEM =
  "tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-text-[13px] tw-text-content-muted";
const FEATURE_ITEM_DISABLED =
  FEATURE_ITEM + " tw-text-content-subtle tw-line-through tw-opacity-45";

// ── Plan cards ──
const UPGRADE_GRID =
  "tw-grid tw-grid-cols-[repeat(auto-fill,minmax(280px,1fr))] tw-gap-4 max-[768px]:tw-grid-cols-1";
const CARD =
  "tw-relative tw-flex tw-flex-col tw-gap-[1.125rem] tw-rounded-lg tw-border tw-border-solid " +
  "tw-border-line tw-bg-surface-raised tw-p-6 tw-transition-[border-color,box-shadow] tw-duration-200 " +
  "hover:tw-border-primary/25 hover:tw-shadow-[0_4px_20px_hsl(var(--primary)/0.08)]";
const CARD_RECOMMENDED =
  "tw-border-primary tw-shadow-[0_4px_24px_hsl(var(--primary)/0.12)] " +
  "hover:tw-shadow-[0_8px_32px_hsl(var(--primary)/0.16)]";
const CARD_CURRENT =
  "tw-border-2 tw-border-primary tw-shadow-[0_0_0_3px_hsl(var(--primary)/0.1)]";
const CARD_DOWNGRADE = "tw-opacity-85";

const RIBBON =
  "tw-absolute tw--top-px tw-right-5 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-b-sm " +
  "tw-px-3 tw-py-[0.3rem] tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-[0.04em]";
// The old `--current` / `--downgrade` ribbons each needed an `!important` to beat
// the base rule's gradient. There is nothing to beat here — the variant simply
// picks its own string.
const RIBBON_BRAND =
  RIBBON + " tw-text-primary-solid-fg " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--primary-solid)),color-mix(in_srgb,hsl(var(--primary-solid))_78%,black))]";
const RIBBON_CURRENT =
  RIBBON + " tw-text-primary-solid-fg " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--primary-solid)),color-mix(in_srgb,hsl(var(--primary-solid))_82%,white))]";
const RIBBON_DOWNGRADE =
  RIBBON + " tw-text-on-status " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--warning)),color-mix(in_srgb,hsl(var(--warning))_78%,black))]";

const CARD_HEADER = "tw-flex tw-flex-col tw-gap-1";
const CARD_NAME = "tw-m-0 tw-text-[17px] tw-font-medium tw-text-content";
const CARD_PRICE = "tw-flex tw-items-baseline tw-gap-1";
const CARD_AMOUNT =
  "tw-text-[1.625rem] tw-font-extrabold tw-leading-none tw-tracking-[-0.02em] tw-text-content";
const CARD_PERIOD = "tw-text-[13px] tw-text-content-subtle";
const CARD_LIST_PRICE =
  "tw-text-[15px] tw-font-medium tw-leading-none tw-text-content-subtle tw-line-through " +
  "tw-decoration-1";
const CARD_DISCOUNT =
  "tw-mt-1 tw-inline-flex tw-items-center tw-gap-1 tw-text-[0.7rem] tw-font-semibold " +
  "tw-text-success-fg";
const CARD_TAX_INFO =
  "tw-mt-0.5 tw-text-[0.7rem] tw-tracking-[0.01em] tw-text-content-subtle";
const CARD_BLOCK =
  "tw-flex tw-flex-col tw-gap-1.5 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-py-3";
const CARD_ROW = "tw-flex tw-items-center tw-justify-between tw-text-[13px]";
const CARD_ROW_LABEL = "tw-text-content-muted";
const CARD_ROW_LABEL_ICONIC = "tw-flex tw-items-center tw-gap-1.5 tw-text-content-muted";
const CARD_ROW_ICON = "tw-shrink-0 tw-text-content-subtle";
const CARD_ROW_VALUE = "tw-font-semibold tw-text-content";
const CARD_NEW_LABEL =
  "tw-mb-0.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-uppercase " +
  "tw-tracking-[0.04em] tw-text-primary-soft-fg";
const CARD_NEW_ITEM =
  "tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-text-content-muted";
const CARD_NEW_ITEM_MORE =
  CARD_NEW_ITEM + " tw-text-xs tw-italic tw-text-content-subtle";
const CARD_NEW_ITEM_ICON = "tw-shrink-0 tw-text-success";
const CARD_LOST =
  "tw-rounded-sm tw-border tw-border-solid tw-border-danger/15 tw-bg-danger/[0.06] " +
  "tw-px-3 tw-py-2.5";
const CARD_LOST_LABEL =
  "tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-danger-fg";
const CARD_LOST_ITEM =
  "tw-flex tw-items-center tw-gap-1.5 tw-py-[2px] tw-text-xs tw-text-danger-fg";
const CARD_LOST_ITEM_MORE =
  "tw-flex tw-items-center tw-gap-1.5 tw-py-[2px] tw-text-xs tw-italic tw-text-content-subtle";

// No background in the base — see the header note. Each variant supplies its own,
// because a gradient is a background-IMAGE and a flat fill is a background-COLOR,
// and the two do not override one another.
const CARD_BTN_BASE =
  "tw-mt-auto tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-md " +
  "tw-border-none tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-transition-all tw-duration-200";
const CARD_BTN_UPGRADE =
  CARD_BTN_BASE + " tw-cursor-pointer tw-text-primary-solid-fg " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--primary-solid)),color-mix(in_srgb,hsl(var(--primary-solid))_82%,white))] " +
  "hover:tw--translate-y-px " +
  "hover:tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--primary-solid))_82%,black),hsl(var(--primary-solid)))] " +
  "hover:tw-shadow-[0_4px_12px_hsl(var(--primary)/0.3)] active:tw-translate-y-0 " +
  "disabled:tw-translate-y-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 disabled:tw-shadow-none";
const CARD_BTN_DOWNGRADE =
  CARD_BTN_BASE + " tw-cursor-pointer tw-text-on-status " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--warning)),color-mix(in_srgb,hsl(var(--warning))_78%,black))] " +
  "hover:tw--translate-y-px " +
  "hover:tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--warning))_78%,black),color-mix(in_srgb,hsl(var(--warning))_62%,black))] " +
  "hover:tw-shadow-[0_4px_12px_hsl(var(--warning)/0.3)] active:tw-translate-y-0 " +
  "disabled:tw-translate-y-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 disabled:tw-shadow-none";
const CARD_BTN_CURRENT =
  CARD_BTN_BASE + " tw-cursor-default tw-bg-surface-alt tw-font-medium tw-text-content-muted";

// ── Payment history ──
const HISTORY_WRAP = "tw-mt-3 tw-overflow-x-auto";
const HISTORY_TABLE = "tw-w-full tw-border-collapse tw-text-[13px]";
const HISTORY_TH =
  "tw-whitespace-nowrap tw-border-0 tw-border-b-2 tw-border-solid tw-border-line tw-px-3 tw-py-2.5 " +
  "tw-text-left tw-font-semibold tw-text-content-muted";
const HISTORY_TD =
  "tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2.5 tw-text-content";
const HISTORY_ROW = "hover:tw-bg-surface-alt";
const PAY_STATUS =
  "tw-inline-block tw-rounded-[6px] tw-px-2 tw-py-[2px] tw-text-[0.7rem] tw-font-semibold tw-uppercase";
const PAY_STATUS_TONE = {
  paid: "tw-bg-success-soft tw-text-success-fg",
  created: "tw-bg-primary-soft tw-text-primary-soft-fg",
  failed: "tw-bg-danger-soft tw-text-danger-fg",
  refunded: "tw-bg-warning-soft tw-text-warning-fg",
};
const RECEIPT_BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-[6px] " +
  "tw-border tw-border-solid tw-border-primary/20 tw-bg-transparent tw-px-2.5 tw-py-[3px] " +
  "tw-text-xs tw-font-medium tw-text-primary-soft-fg tw-transition-all tw-duration-150 " +
  "hover:tw-border-primary/30 hover:tw-bg-primary/[0.06]";
const EMPTY_HISTORY = "tw-py-6 tw-text-center tw-text-sm tw-text-content-subtle";

// ── Negotiated-pricing note ──
// The rgba() greens the old sheet's header said had "no token to reach for" were
// --success at those same two alphas all along.
const NEGOTIATED_NOTE =
  "tw-mb-3.5 tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid " +
  "tw-border-success/15 tw-bg-success/[0.06] tw-px-3 tw-py-2 tw-text-[13px] tw-leading-[1.4] " +
  "tw-text-content-muted";
const NEGOTIATED_ICON = "tw-shrink-0 tw-text-success-fg";

// ── Help card ──
const HELP_CARD =
  "tw-flex tw-items-center tw-justify-between tw-gap-6 tw-rounded-lg tw-border tw-border-solid " +
  "tw-border-primary/10 tw-bg-primary/[0.04] tw-px-8 tw-py-6 " +
  "max-[768px]:tw-flex-col max-[768px]:tw-p-5 max-[768px]:tw-text-center";
const HELP_TITLE = "tw-mb-1 tw-mt-0 tw-text-base tw-font-medium tw-text-content";
const HELP_TEXT = "tw-m-0 tw-text-[13px] tw-text-content-subtle";
const HELP_BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md " +
  "tw-border tw-border-solid tw-border-primary/20 tw-bg-surface-raised tw-px-5 tw-py-2 " +
  "tw-text-[13px] tw-font-semibold tw-text-primary-soft-fg tw-transition-all tw-duration-150 " +
  "hover:tw-border-primary/30 hover:tw-bg-primary/[0.06]";

// ── Empty / error state ──
const EMPTY = "tw-px-8 tw-py-16 tw-text-center tw-text-content-subtle";
const EMPTY_ICON = "tw-mb-4 tw-text-content-subtle";
const EMPTY_TITLE = "tw-mb-2 tw-text-lg tw-font-semibold tw-text-content";
const EMPTY_TEXT = "tw-m-0 tw-text-sm";

// ── Labels ──
const LIMIT_LABELS = {
  maxStudentsPerBatch: { label: "Students/Batch", icon: Users },
  maxBatches: { label: "Batches", icon: Layers },
  maxCourses: { label: "Courses", icon: BookOpen },
  maxInstructors: { label: "Instructors", icon: UserCog },
  maxAdmins: { label: "Admins", icon: Shield },
  maxBranches: { label: "Branches", icon: Layers },
};

const USAGE_KEYS = {
  batches: "maxBatches",
  courses: "maxCourses",
  instructors: "maxInstructors",
  admins: "maxAdmins",
  branches: "maxBranches",
};

const TOGGLE_LABELS = {
  zoomV2: "Zoom V2 Integration",
  recordings: "Class Recordings",
  whatsapp: "WhatsApp Notifications",
  customDomain: "Custom Domain",
  aiMock: "AI Mock Interviews",
  aiLearning: "AI Learning Assistant",
  aiQuestionBank: "AI Question Bank",
  feeManagement: "Fee Management",
  enquiryManagement: "Enquiry Management",
  interviews: "Interview Scheduling",
  codingPractice: "Coding Practice",
  assignments: "Assignments",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  enquiryFormBuilder: "Enquiry Form Builder",
  multiBranch: "Multi-Branch Management",
};

const formatPrice = (amount) => {
  if (!amount || amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
};

// ── Usage Meter (single resource) ──
function UsageMeter({ label, icon: Icon, used, max }) {
  const isUnlimited = !max || max === 0;
  const percent = isUnlimited ? 0 : Math.min(Math.round((used / max) * 100), 100);
  const isWarning = percent >= 80 && percent < 95;
  const isDanger = percent >= 95;
  const tone = isDanger ? "danger" : isWarning ? "warning" : "normal";

  return (
    <div className={USAGE_METER}>
      <div className={USAGE_HEADER}>
        <div className={USAGE_LABEL}>
          <Icon size={16} className={USAGE_LABEL_ICON} />
          <span>{label}</span>
        </div>
        <span className={USAGE_COUNT}>
          <strong className={USAGE_COUNT_STRONG}>{used}</strong>
          {isUnlimited ? " (Unlimited)" : ` / ${max}`}
        </span>
      </div>
      {!isUnlimited && (
        <div className={USAGE_TRACK}>
          <div
            className={`${USAGE_FILL} ${USAGE_FILL_TONE[tone]}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {isDanger && (
        <div className={USAGE_ALERT}>
          <AlertTriangle size={12} /> Almost at limit — upgrade to add more
        </div>
      )}
    </div>
  );
}

// ── Feature Badge (current plan feature list) ──
function FeatureItem({ label, enabled }) {
  return (
    <div className={enabled ? FEATURE_ITEM : FEATURE_ITEM_DISABLED}>
      {enabled ? (
        <Check size={14} className="tw-shrink-0 tw-text-success" />
      ) : (
        <X size={14} className="tw-shrink-0 tw-text-content-subtle" />
      )}
      <span>{label}</span>
    </div>
  );
}

// ── Plan Card (supports current / upgrade / downgrade) ──
function PlanCard({ plan, currentPlan, billingCycle, taxConfig, onSelect, paymentLoading }) {
  const tag = plan._planTag || "upgrade"; // 'current' | 'upgrade' | 'downgrade' | 'current-tier'
  const isCurrent = tag === "current" || tag === "current-tier";
  const isDowngrade = tag === "downgrade";
  const isNext = plan.isPopular && !isCurrent;
  const cycle = billingCycle || "monthly";
  const priceKey =
    cycle === "yearly" ? "priceYearly" : cycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
  const periodLabel =
    cycle === "yearly" ? "/year" : cycle === "quarterly" ? "/quarter" : "/month";

  // The price comes from the server (utils/planPricing.js), never from arithmetic
  // here. When the institute has a negotiated price, `payable` is below
  // `listPrice` and the card strikes the latter through. `plan[priceKey]` is the
  // fallback for any response predating that field — it is the list price, which
  // is the safe thing to show if the resolved figure is missing.
  const resolved = plan.pricing?.[cycle] || null;
  const listPrice = resolved ? resolved.listPrice : (plan[priceKey] || 0);
  const basePrice = resolved ? resolved.payable : (plan[priceKey] || 0);
  const discountAmount = resolved?.discountAmount || 0;
  const discountLabel = resolved?.discount?.label || "";
  const hasTax = taxConfig?.enabled && taxConfig.percentage > 0;

  // Tax is displayed on the DISCOUNTED price, matching what checkout will charge.
  let displaySubtotal = basePrice;
  let displayTax = 0;
  let displayTotal = basePrice;

  if (hasTax) {
    if (taxConfig.includeInPrice) {
      displayTotal = basePrice;
      displaySubtotal = Math.round(basePrice / (1 + taxConfig.percentage / 100));
      displayTax = displayTotal - displaySubtotal;
    } else {
      displaySubtotal = basePrice;
      displayTax = Math.round(basePrice * taxConfig.percentage / 100);
      displayTotal = displaySubtotal + displayTax;
    }
  }

  // Count how many MORE features this plan unlocks vs current
  const newToggles = Object.keys(TOGGLE_LABELS).filter(
    (k) => plan[k] && !currentPlan?.[k]
  );
  // Features that would be LOST on downgrade
  const lostToggles = Object.keys(TOGGLE_LABELS).filter(
    (k) => currentPlan?.[k] && !plan[k]
  );

  const cardClass = [
    CARD,
    isNext ? CARD_RECOMMENDED : "",
    isCurrent ? CARD_CURRENT : "",
    isDowngrade ? CARD_DOWNGRADE : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClass}>
      {isCurrent && (
        <div className={RIBBON_CURRENT}>
          <Crown size={12} /> Current Plan
        </div>
      )}
      {isNext && !isCurrent && (
        <div className={RIBBON_BRAND}>
          <Star size={12} /> Recommended
        </div>
      )}
      {isDowngrade && !isCurrent && (
        <div className={RIBBON_DOWNGRADE}>
          <ArrowDown size={12} /> Downgrade
        </div>
      )}
      <div className={CARD_HEADER}>
        <h3 className={CARD_NAME}>{plan.name}</h3>
        {plan.isFree ? (
          <div className={CARD_PRICE}>
            <span className={CARD_AMOUNT}>Free</span>
          </div>
        ) : (
          <div className={CARD_PRICE}>
            {discountAmount > 0 && (
              <span className={CARD_LIST_PRICE}>{formatPrice(listPrice)}</span>
            )}
            <span className={CARD_AMOUNT}>{formatPrice(displayTotal)}</span>
            <span className={CARD_PERIOD}>{periodLabel}</span>
          </div>
        )}
        {discountAmount > 0 && !plan.isFree && (
          <div className={CARD_DISCOUNT}>
            <Tag size={12} className="tw-shrink-0" /> {discountLabel || "Your price"}
          </div>
        )}
        {hasTax && !plan.isFree && (
          <div className={CARD_TAX_INFO}>
            {formatPrice(displaySubtotal)} + {taxConfig.label} {taxConfig.percentage}% ({formatPrice(displayTax)})
          </div>
        )}
      </div>

      <div className={CARD_BLOCK}>
        {Object.entries(LIMIT_LABELS).map(([key, { label }]) => (
          <div key={key} className={CARD_ROW}>
            <span className={CARD_ROW_LABEL}>{label}</span>
            <strong className={CARD_ROW_VALUE}>{plan[key] || "Unlimited"}{key === "storageGB" && plan[key] ? " GB" : ""}</strong>
          </div>
        ))}
      </div>

      <div className={CARD_BLOCK}>
        <div className={CARD_ROW}>
          <span className={CARD_ROW_LABEL_ICONIC}><HardDrive size={14} className={CARD_ROW_ICON} /> Storage</span>
          <strong className={CARD_ROW_VALUE}>{plan.storageGB ? `${plan.storageGB} GB` : "Unlimited"}</strong>
        </div>
        <div className={CARD_ROW}>
          <span className={CARD_ROW_LABEL_ICONIC}><Percent size={14} className={CARD_ROW_ICON} /> Revenue Share</span>
          <strong className={CARD_ROW_VALUE}>{plan.revenueSharePercent ? `${plan.revenueSharePercent}%` : "0%"}</strong>
        </div>
        {plan.extraStoragePricePer100GB > 0 && (
          <div className={CARD_ROW}>
            <span className={CARD_ROW_LABEL_ICONIC}><BadgeIndianRupee size={14} className={CARD_ROW_ICON} /> Extra Storage</span>
            <strong className={CARD_ROW_VALUE}>{formatPrice(plan.extraStoragePricePer100GB)} / 100 GB</strong>
          </div>
        )}
      </div>

      {newToggles.length > 0 && !isCurrent && (
        <div className={CARD_BLOCK}>
          <div className={CARD_NEW_LABEL}>
            <Sparkles size={12} /> {isDowngrade ? "Includes" : "Unlocks"} {newToggles.length} new feature{newToggles.length > 1 ? "s" : ""}
          </div>
          {newToggles.slice(0, 4).map((k) => (
            <div key={k} className={CARD_NEW_ITEM}>
              <Check size={12} className={CARD_NEW_ITEM_ICON} /> {TOGGLE_LABELS[k]}
            </div>
          ))}
          {newToggles.length > 4 && (
            <div className={CARD_NEW_ITEM_MORE}>
              +{newToggles.length - 4} more
            </div>
          )}
        </div>
      )}

      {isDowngrade && lostToggles.length > 0 && (
        <div className={CARD_LOST}>
          <div className={CARD_LOST_LABEL}>
            <AlertTriangle size={12} /> You will lose {lostToggles.length} feature{lostToggles.length > 1 ? "s" : ""}
          </div>
          {lostToggles.slice(0, 3).map((k) => (
            <div key={k} className={CARD_LOST_ITEM}>
              <X size={12} /> {TOGGLE_LABELS[k]}
            </div>
          ))}
          {lostToggles.length > 3 && (
            <div className={CARD_LOST_ITEM_MORE}>
              +{lostToggles.length - 3} more
            </div>
          )}
        </div>
      )}

      {isCurrent ? (
        <div className={CARD_BTN_CURRENT}>
          <Check size={14} /> Your Current Plan
        </div>
      ) : onSelect && (
        <button
          className={isDowngrade ? CARD_BTN_DOWNGRADE : CARD_BTN_UPGRADE}
          onClick={() => onSelect?.(plan)}
          disabled={paymentLoading || plan.isFree}
        >
          {isDowngrade ? (
            <><ArrowDown size={14} /> Downgrade to {plan.name}</>
          ) : (
            <><ArrowRight size={14} /> Upgrade to {plan.name}</>
          )}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// Main Page
// ═══════════════════════════════════════
const MyPlanPage = () => {
  const navigate = useNavigate();
  const canBuy = can("billing.purchase");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get("/billing/my-plan");
      setData(res);
      if (res.subscription?.billingCycle) {
        setBillingCycle(res.subscription.billingCycle);
      }
    } catch (err) {
      console.error("Failed to load billing info:", err);
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // ── Fetch payment history ──
  const fetchPaymentHistory = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get("/billing/payment-history");
      setPayments(res.payments || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  // On a custom domain the platform Razorpay account refuses the in-page
  // checkout, so the plan is paid in a new tab on the platform's own domain.
  const hostedCheckout = useHostedCheckout({
    onPaid: () => {
      setPaymentMessage({ type: "success", text: "Payment received — your plan is active." });
      fetchPlan();
      fetchPaymentHistory();
    },
  });

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await apiClient.get(`/billing/payment-receipt/${paymentId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setPaymentMessage({ type: "error", text: "Failed to download receipt." });
    }
  };

  // ── Load Razorpay Checkout script dynamically ──
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgradeSelect = async (plan) => {
    if (paymentLoading) return;

    // Free plan — no payment needed
    const priceKey = billingCycle === "yearly" ? "priceYearly" : billingCycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
    if (!plan[priceKey] || plan[priceKey] === 0) {
      setPaymentMessage({ type: "error", text: "This plan is free. Contact support if you want to switch." });
      return;
    }

    if (hostedCheckout.required) {
      setPaymentMessage(null);
      hostedCheckout.start("/billing/hosted-checkout", { planId: plan._id, billingCycle });
      return;
    }

    setPaymentLoading(true);
    setPaymentMessage(null);

    try {
      // 1. Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPaymentMessage({ type: "error", text: "Failed to load payment gateway. Check your internet connection." });
        setPaymentLoading(false);
        return;
      }

      // 2. Create order on backend
      const { data: orderData } = await apiClient.post("/billing/create-order", {
        planId: plan._id,
        billingCycle,
      });

      // 3. Open Razorpay checkout
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Build description with tax info if present
      const taxInfo = orderData.tax;
      let description = `${orderData.plan?.name} — ${orderData.plan?.billingCycle} plan`;
      if (taxInfo?.taxAmount > 0) {
        description += ` (incl. ${taxInfo.taxLabel} ${taxInfo.taxPercentage}%)`;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.institute?.name || "Institute",
        description,
        order_id: orderData.orderId,
        prefill: {
          name: user.name || "",
          email: user.email || "",
          contact: user.phone || "",
        },
        // Razorpay's checkout is a third-party iframe configured with a HEX — it
  // cannot read a CSS custom property, so this is the one accent on the page
  // that stays a literal. Same category as the --ext-* brand marks.
  theme: { color: "#6366f1" },
        handler: async (response) => {
          // 4. Verify payment on backend
          try {
            const { data: verifyRes } = await apiClient.post("/billing/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaymentMessage({ type: "success", text: verifyRes.message || "Plan upgraded successfully!" });
            // Refresh plan data
            fetchPlan();
          } catch (verifyErr) {
            console.error("Payment verification failed:", verifyErr);
            setPaymentMessage({
              type: "error",
              text: verifyErr?.response?.data?.message || "Payment verification failed. Contact support if amount was deducted.",
            });
          }
          setPaymentLoading(false);
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            setPaymentMessage({ type: "error", text: "Payment cancelled." });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setPaymentLoading(false);
        setPaymentMessage({
          type: "error",
          text: response.error?.description || "Payment failed. Please try again.",
        });
      });
      rzp.open();
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setPaymentMessage({
        type: "error",
        text: err?.response?.data?.message || "Could not initiate payment. Please try again.",
      });
      setPaymentLoading(false);
    }
  };

  // ── Loading state ──
  // A Shimmer mirroring the layout, not a spinner (Phase 6.5): header, hero,
  // the five usage meters, then the plan cards. The counts match what the loaded
  // page renders, so the skeleton is the same shape as what replaces it.
  if (loading) {
    return (
      <div className={PAGE}>
        <Shimmer height={44} />
        <Shimmer height={190} />
        <div className={USAGE_GRID}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Shimmer key={i} height={92} />
          ))}
        </div>
        <div className={UPGRADE_GRID}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Shimmer key={i} height={340} />
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (!data) {
    return (
      <div className={PAGE}>
        <div className={EMPTY}>
          <AlertTriangle size={40} className={EMPTY_ICON} />
          <h3 className={EMPTY_TITLE}>Unable to load plan information</h3>
          <p className={EMPTY_TEXT}>Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  const { currentPlan, usage, subscription, upgradePlans = [], taxConfig, pendingPayment, discount, managedBy } = data;
  const isTrialActive = subscription?.status === "trial";
  const isExpired = subscription?.daysRemaining === 0 && subscription?.status !== "active";
  const priceKey =
    billingCycle === "yearly" ? "priceYearly" : billingCycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
  const currentPlanHasPrice = currentPlan && currentPlan[priceKey] > 0;
  const periodLabel =
    billingCycle === "yearly" ? "/year" : billingCycle === "quarterly" ? "/quarter" : "/month";

  // Pay the SA-marked pending dues — reuse the existing upgrade flow with the
  // institute's currently-assigned plan + saved billing cycle so the Razorpay
  // amount matches what getMyPlan computed.
  const handlePayPending = async () => {
    if (!pendingPayment || !currentPlan) return;
    // Lock the billing cycle to the one the SA chose so the order amount matches.
    if (pendingPayment.billingCycle && pendingPayment.billingCycle !== billingCycle) {
      setBillingCycle(pendingPayment.billingCycle);
    }
    // handleUpgradeSelect reads billingCycle from state. Use a closure that
    // forces the correct cycle by temporarily overriding via a dedicated call.
    await handleUpgradeSelectForCycle(currentPlan, pendingPayment.billingCycle || billingCycle);
  };

  // Variant of handleUpgradeSelect that takes an explicit cycle (used by Pay Pending).
  const handleUpgradeSelectForCycle = async (plan, cycle) => {
    if (paymentLoading) return;
    const pk = cycle === "yearly" ? "priceYearly" : cycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
    if (!plan[pk] || plan[pk] === 0) {
      setPaymentMessage({ type: "error", text: "This plan has no price configured for the selected billing cycle." });
      return;
    }
    if (hostedCheckout.required) {
      setPaymentMessage(null);
      hostedCheckout.start("/billing/hosted-checkout", { planId: plan._id, billingCycle: cycle });
      return;
    }
    setPaymentLoading(true);
    setPaymentMessage(null);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPaymentMessage({ type: "error", text: "Failed to load payment gateway. Check your internet connection." });
        setPaymentLoading(false);
        return;
      }
      const { data: orderData } = await apiClient.post("/billing/create-order", {
        planId: plan._id,
        billingCycle: cycle,
      });
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const taxInfo = orderData.tax;
      let description = `${orderData.plan?.name} — ${orderData.plan?.billingCycle} plan`;
      if (taxInfo?.taxAmount > 0) {
        description += ` (incl. ${taxInfo.taxLabel} ${taxInfo.taxPercentage}%)`;
      }
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.institute?.name || "Institute",
        description,
        order_id: orderData.orderId,
        prefill: { name: user.name || "", email: user.email || "", contact: user.phone || "" },
        // Razorpay's checkout is a third-party iframe configured with a HEX — it
  // cannot read a CSS custom property, so this is the one accent on the page
  // that stays a literal. Same category as the --ext-* brand marks.
  theme: { color: "#6366f1" },
        handler: async (response) => {
          try {
            const { data: verifyRes } = await apiClient.post("/billing/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaymentMessage({ type: "success", text: verifyRes.message || "Payment successful!" });
            fetchPlan();
          } catch (verifyErr) {
            setPaymentMessage({
              type: "error",
              text: verifyErr?.response?.data?.message || "Payment verification failed. Contact support if amount was deducted.",
            });
          }
          setPaymentLoading(false);
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            setPaymentMessage({ type: "error", text: "Payment cancelled." });
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setPaymentLoading(false);
        setPaymentMessage({ type: "error", text: response.error?.description || "Payment failed. Please try again." });
      });
      rzp.open();
    } catch (err) {
      setPaymentMessage({
        type: "error",
        text: err?.response?.data?.message || "Could not initiate payment. Please try again.",
      });
      setPaymentLoading(false);
    }
  };


  return (
    <div className={PAGE}>
      {/* ── Page Header ── */}
      <div className={HEADER}>
        <div className={HEADER_LEFT}>
          <div className={HEADER_ICON}>
            <Crown size={22} />
          </div>
          <div>
            <h1 className={HEADER_TITLE}>Plan &amp; Billing</h1>
            <p className={HEADER_SUBTITLE}>
              {managedBy
                ? "Your plan, its features, and how much of it you are using"
                : "Manage your subscription, track resource usage, and explore upgrades"}
            </p>
          </div>
        </div>
      </div>

      {/* ── Managed by a distributor ── no prices, no purchases: the
          distributor manages this plan and pays for it. */}
      {managedBy && (
        <div className={`${ALERT} ${ALERT_TONE.info}`}>
          <Handshake size={18} className="tw-shrink-0" />
          <div className={ALERT_BODY}>
            <strong>Your plan is managed by {managedBy.name}</strong>
            <span className={ALERT_BODY_SPAN}>
              To change your plan or add features, contact them.
            </span>
            {(managedBy.contacts || []).map((c, i) => (
              <span key={i} className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-xs">
                {c.name && <b>{c.name}</b>}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-inherit">
                    <Mail size={12} aria-hidden="true" /> {c.email}
                  </a>
                )}
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-inherit">
                    <Phone size={12} aria-hidden="true" /> {c.phone}
                  </a>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Trial / Expiry Alert ── */}
      {isTrialActive && (
        <div className={`${ALERT} ${ALERT_TONE.trial}`}>
          <Clock size={18} className="tw-shrink-0" />
          <div className={ALERT_BODY}>
            <strong>Free Trial — {subscription.daysRemaining} day{subscription.daysRemaining !== 1 ? "s" : ""} remaining</strong>
            <span className={ALERT_BODY_SPAN}>{currentPlanHasPrice ? "Subscribe now to activate your plan, or upgrade for more features." : "Upgrade now to keep your data and unlock all features."}</span>
          </div>
          {canBuy && (
            <div className={ALERT_ACTIONS}>
              {currentPlanHasPrice && (
                <button
                  className={ALERT_CTA_PRIMARY}
                  onClick={() => handleUpgradeSelect(currentPlan)}
                  disabled={paymentLoading}
                >
                  Subscribe to {currentPlan.name} <ArrowRight size={14} />
                </button>
              )}
              <a href="#upgrade" className={ALERT_CTA}>Upgrade Plan <ArrowRight size={14} /></a>
            </div>
          )}
        </div>
      )}
      {isExpired && (
        <div className={`${ALERT} ${ALERT_TONE.danger}`}>
          <AlertTriangle size={18} className="tw-shrink-0" />
          <div className={ALERT_BODY}>
            <strong>Your subscription has expired</strong>
            <span className={ALERT_BODY_SPAN}>{currentPlanHasPrice ? "Renew your current plan or upgrade to a new one." : "Renew immediately to prevent data access interruptions."}</span>
          </div>
          {canBuy && (
            <div className={ALERT_ACTIONS}>
              {currentPlanHasPrice && (
                <button
                  className={ALERT_CTA_PRIMARY}
                  onClick={() => handleUpgradeSelect(currentPlan)}
                  disabled={paymentLoading}
                >
                  Renew {currentPlan.name} <ArrowRight size={14} />
                </button>
              )}
              <a href="#upgrade" className={ALERT_CTA}>Upgrade Plan <ArrowRight size={14} /></a>
            </div>
          )}
        </div>
      )}

      {/* ── Payment Status Message ── */}
      {paymentMessage && (
        <div className={`${ALERT} ${paymentMessage.type === "success" ? ALERT_TONE.success : ALERT_TONE.danger}`}>
          {paymentMessage.type === "success"
            ? <Check size={18} className="tw-shrink-0" />
            : <AlertTriangle size={18} className="tw-shrink-0" />}
          <div className={ALERT_BODY}>
            <strong>{paymentMessage.text}</strong>
          </div>
          <button className={ALERT_CLOSE} onClick={() => setPaymentMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* ── Payment Loading Overlay ── */}
      {/* Still a spinner, deliberately: a redirect to a payment gateway is the
          genuinely indeterminate wait a spinner is for. */}
      {paymentLoading && (
        <div className={`${ALERT} ${ALERT_TONE.info}`}>
          <Loader size={18} className="tw-shrink-0 tw-animate-spin" />
          <div className={ALERT_BODY}>
            <strong>Processing payment... Please do not close this page.</strong>
          </div>
        </div>
      )}

      {/* ── Current Plan Hero ── */}
      <div className={HERO}>
        {/* Pending payment alert — shown when SaaS admin activated the plan as "unpaid" */}
        {pendingPayment && (
          <div className={PENDING}>
            <div className={PENDING_ICON}>
              <AlertTriangle size={18} />
            </div>
            <div className={PENDING_BODY}>
              <strong className={PENDING_TITLE}>Payment pending for {pendingPayment.planName}</strong>
              <span className={PENDING_TEXT}>
                Your plan is active. Please complete payment of{" "}
                <b>{formatPrice(pendingPayment.amount)}</b>
                {pendingPayment.taxAmount > 0 && pendingPayment.taxLabel && (
                  <> (incl. {pendingPayment.taxLabel} {pendingPayment.taxPercentage}%)</>
                )}
                {" "}for the {pendingPayment.billingCycle} cycle.
              </span>
              {/* The negotiated price is stated on the line that asks for money.
                  A customer who agreed a figure on a call checks it here before
                  paying, and "why is this not the price we agreed" is the support
                  ticket this line exists to prevent. */}
              {pendingPayment.discountAmount > 0 && (
                <span className={PENDING_DISCOUNT}>
                  <Tag size={12} className="tw-shrink-0" /> {pendingPayment.discountLabel || "Special pricing"} applied —{" "}
                  <s className="tw-text-content-subtle">{formatPrice(pendingPayment.listPrice)}</s>{" "}
                  <b className="tw-font-bold tw-text-content">{formatPrice(pendingPayment.listPrice - pendingPayment.discountAmount)}</b>
                  {" "}before {pendingPayment.taxLabel || "tax"}.
                </span>
              )}
            </div>
            {canBuy && (
              <button
                className={PENDING_PAY_BTN}
                onClick={handlePayPending}
                disabled={paymentLoading}
              >
                <BadgeIndianRupee size={14} /> {paymentLoading ? "Processing…" : `Pay ${formatPrice(pendingPayment.amount)}`}
              </button>
            )}
          </div>
        )}

        <div className={HERO_TOP}>
          <div className={HERO_INFO}>
            <div className={HERO_NAME_ROW}>
              <span className={HERO_PLAN_NAME}>{currentPlan?.name || "No Plan"}</span>
              <span className={`${HERO_STATUS} ${HERO_STATUS_TONE[subscription?.status] || HERO_STATUS_TONE.inactive}`}>
                {subscription?.status === "active" ? "Active" : subscription?.status === "trial" ? "Trial" : subscription?.status === "suspended" ? "Suspended" : "Inactive"}
              </span>
            </div>
            {!managedBy && (
              <div className={HERO_PRICE}>
                <span className={HERO_AMOUNT}>
                  {currentPlan ? formatPrice(currentPlan[priceKey]) : "—"}
                </span>
                {currentPlan && <span className={HERO_PERIOD}>{periodLabel}</span>}
              </div>
            )}
          </div>
          <div className={HERO_DATES}>
            {subscription?.startDate && (
              <div className={HERO_DATE_ITEM}>
                <Calendar size={14} className={HERO_DATE_ICON} />
                <span>Started {formatDate(subscription.startDate)}</span>
              </div>
            )}
            {subscription?.endDate && (
              <div className={HERO_DATE_ITEM}>
                <Clock size={14} className={HERO_DATE_ICON} />
                <span>Renews {formatDate(subscription.endDate)}</span>
              </div>
            )}
            {isTrialActive && subscription?.trialExpiresAt && (
              <div className={HERO_DATE_ITEM}>
                <AlertTriangle size={14} className={HERO_DATE_ICON} />
                <span>Trial ends {formatDate(subscription.trialExpiresAt)}</span>
              </div>
            )}
            {/* Renew button — monthly: 3 days before, quarterly: 7 days, yearly: 14 days */}
            {canBuy && currentPlan && currentPlanHasPrice && !isTrialActive && (() => {
              const cycle = subscription?.billingCycle;
              const threshold = cycle === 'yearly' ? 14 : cycle === 'quarterly' ? 7 : 3;
              return (subscription?.daysRemaining != null && subscription.daysRemaining <= threshold) ||
                isExpired || subscription?.inGracePeriod;
            })() && (
              <button
                className={HERO_RENEW_BTN}
                onClick={() => handleUpgradeSelect(currentPlan)}
                disabled={paymentLoading}
              >
                <Zap size={14} /> Renew {currentPlan.name}
              </button>
            )}
          </div>
        </div>

        {/* ── Billing Cycle Toggle ── */}
        {!managedBy && (
        <div className={CYCLE_TOGGLE}>
          {["monthly", "quarterly", "yearly"].map((c) => (
            <button
              key={c}
              className={billingCycle === c ? CYCLE_BTN_ACTIVE : CYCLE_BTN}
              onClick={() => setBillingCycle(c)}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
              {c === "yearly" && <span className={CYCLE_SAVE}>Save 20%</span>}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* ── Resource Usage ── */}
      <div className={SECTION}>
        <div className={SECTION_HEADER}>
          <TrendingUp size={18} className={SECTION_ICON} />
          <h2 className={SECTION_TITLE}>Resource Usage</h2>
        </div>
        <div className={USAGE_GRID}>
          {Object.entries(USAGE_KEYS).map(([usageKey, limitKey]) => {
            const u = usage?.[usageKey];
            const meta = LIMIT_LABELS[limitKey];
            if (!u || !meta) return null;
            return (
              <UsageMeter
                key={usageKey}
                label={meta.label}
                icon={meta.icon}
                used={u.used}
                max={u.max}
              />
            );
          })}
        </div>
      </div>

      {/* ── Current Features ── */}
      {currentPlan && (
        <div className={SECTION}>
          <div className={SECTION_HEADER}>
            <Zap size={18} className={SECTION_ICON} />
            <h2 className={SECTION_TITLE}>Your Features</h2>
          </div>
          <div className={FEATURES_GRID}>
            {Object.entries(TOGGLE_LABELS).map(([key, label]) => (
              <FeatureItem key={key} label={label} enabled={!!currentPlan[key]} />
            ))}
          </div>
        </div>
      )}

      {/* ── All Plans Grid ── */}
      {upgradePlans.length > 0 && (
        <div className={SECTION} id="upgrade">
          <div className={SECTION_HEADER}>
            <Sparkles size={18} className={SECTION_ICON} />
            <h2 className={SECTION_TITLE}>Choose Your Plan</h2>
            <p className={SECTION_SUBTITLE}>
              Compare all plans and pick the best fit for your institute
            </p>
          </div>
          {/* Standing note, not just per-card decoration. The strike-through on a
              card explains one number; this explains why every number on the page
              differs from the public pricing site, which is the question a
              customer's finance team asks. */}
          {discount && (
            <div className={NEGOTIATED_NOTE}>
              <Tag size={14} className={NEGOTIATED_ICON} />
              <span>
                <strong className="tw-font-semibold tw-text-success-fg">{discount.label || "Special pricing"}</strong> is applied to your account
                {discount.appliesTo === "first_payment" ? " for your next payment" : ""}
                {discount.validUntil ? ` until ${formatDate(discount.validUntil)}` : ""}.
              </span>
            </div>
          )}
          <div className={UPGRADE_GRID}>
            {upgradePlans.map((plan) => (
              <PlanCard
                key={plan._id}
                plan={plan}
                currentPlan={currentPlan}
                billingCycle={billingCycle}
                taxConfig={taxConfig}
                onSelect={canBuy ? handleUpgradeSelect : undefined}
                paymentLoading={paymentLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Payment History ── (none for a distributor's client: it pays nothing here) */}
      {!managedBy && (
      <div className={SECTION}>
        <div className={SECTION_HEADER_CLICKABLE} onClick={() => setShowHistory((p) => !p)}>
          <History size={18} className={SECTION_ICON} />
          <h2 className={SECTION_TITLE}>Payment History</h2>
          <ChevronRight size={16} className={`${SECTION_CHEVRON} ${showHistory ? "tw-rotate-90" : ""}`} />
        </div>
        {showHistory && (
          payments.length > 0 ? (
            <div className={HISTORY_WRAP}>
              <table className={HISTORY_TABLE}>
                <thead>
                  <tr>
                    <th className={HISTORY_TH}>Date</th>
                    <th className={HISTORY_TH}>Plan</th>
                    <th className={HISTORY_TH}>Cycle</th>
                    <th className={HISTORY_TH}>Amount</th>
                    <th className={HISTORY_TH}>Status</th>
                    <th className={HISTORY_TH}>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p._id} className={HISTORY_ROW}>
                      <td className={HISTORY_TD}>{formatDate(p.createdAt)}</td>
                      <td className={HISTORY_TD}>{p.planId?.name || p.planName || "—"}</td>
                      <td className={`${HISTORY_TD} tw-capitalize`}>{p.billingCycle}</td>
                      <td className={HISTORY_TD}>{formatPrice(p.amount ? p.amount / 100 : 0)}</td>
                      <td className={HISTORY_TD}>
                        <span className={`${PAY_STATUS} ${PAY_STATUS_TONE[p.status] || PAY_STATUS_TONE.created}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className={HISTORY_TD}>
                        {p.status === "paid" && (
                          <button className={RECEIPT_BTN} onClick={() => handleDownloadReceipt(p._id)}>
                            <Download size={14} /> Receipt
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={EMPTY_HISTORY}>No payments yet.</p>
          )
        )}
      </div>
      )}

      {/* ── Need Help ── (a distributor's client asks its distributor, above) */}
      {!managedBy && (
      <div className={HELP_CARD}>
        <div>
          <h3 className={HELP_TITLE}>Need a custom plan?</h3>
          <p className={HELP_TEXT}>Contact our team for enterprise pricing, custom limits, and priority support.</p>
          <p className={HELP_TEXT}>Mail: <a href="mailto:support@lmscampus.com">support@lmscampus.com</a></p>
        </div>
        <button className={HELP_BTN} onClick={() => window.open("mailto:support@lmscampus.com")}>
          Contact Sales <ChevronRight size={14} />
        </button>
      </div>
      )}

      <HostedCheckoutModal checkout={hostedCheckout} />
    </div>
  );
};

export default MyPlanPage;
